import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Badge,
  Button,
} from '@etherisc/ui-kit';
import { RefreshCw, Calendar, Lock, Unlock, PlayCircle } from 'lucide-react';
import { useLedgerReports } from '../../hooks/ledger/useLedgerReports';
import { PermissionGate } from '../../components/PermissionGate';
import { formatDate } from '../../utils/formatters';

interface Period {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'OPEN' | 'CLOSED' | 'LOCKED';
}

export function PeriodManagement() {
  const { periods, periodsLoading, periodsError, refreshPeriods } = useLedgerReports();

  if (periodsError) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>Error loading periods: {periodsError}</p>
              <Button onClick={refreshPeriods} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (periodsLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">Loading periods...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const periodList = periods as Period[] || [];
  const openPeriods = periodList.filter(p => p.status === 'OPEN');
  const closedPeriods = periodList.filter(p => p.status === 'CLOSED');
  const lockedPeriods = periodList.filter(p => p.status === 'LOCKED');

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'OPEN': return 'success';
      case 'CLOSED': return 'secondary';
      case 'LOCKED': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'OPEN': return <PlayCircle className="h-4 w-4" />;
      case 'CLOSED': return <Calendar className="h-4 w-4" />;
      case 'LOCKED': return <Lock className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Period Management</h1>
          <p className="text-muted-foreground">
            Manage accounting periods and their lifecycle
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={refreshPeriods} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <PermissionGate permission="ledger:admin">
            <Button>
              <Calendar className="h-4 w-4 mr-2" />
              Create Period
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Periods</CardTitle>
            <PlayCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{openPeriods.length}</div>
            <p className="text-xs text-muted-foreground">
              Available for posting
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Closed Periods</CardTitle>
            <Calendar className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{closedPeriods.length}</div>
            <p className="text-xs text-muted-foreground">
              Finalized periods
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Locked Periods</CardTitle>
            <Lock className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{lockedPeriods.length}</div>
            <p className="text-xs text-muted-foreground">
              Permanently locked
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Periods List */}
      <Card>
        <CardHeader>
          <CardTitle>All Periods ({periodList.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {periodList.length > 0 ? (
            <div className="space-y-2">
              {periodList.map((period) => (
                <div key={period.id} className="flex items-center justify-between p-4 border rounded hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    {getStatusIcon(period.status)}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{period.name}</span>
                        <Badge variant={getStatusVariant(period.status)}>
                          {period.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">
                        {formatDate(period.startDate)} - {formatDate(period.endDate)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <PermissionGate permission="ledger:admin">
                      {period.status === 'OPEN' && (
                        <Button variant="outline" size="sm">
                          <Calendar className="h-4 w-4 mr-1" />
                          Close
                        </Button>
                      )}
                      {period.status === 'CLOSED' && (
                        <>
                          <Button variant="outline" size="sm">
                            <Unlock className="h-4 w-4 mr-1" />
                            Reopen
                          </Button>
                          <Button variant="outline" size="sm">
                            <Lock className="h-4 w-4 mr-1" />
                            Lock
                          </Button>
                        </>
                      )}
                    </PermissionGate>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No periods found
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
